import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import './Contact.css';
import usePageSearchIndex from '../hooks/usePageSearchIndex';

const Contact = () => {
  const { t, isMarathi } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [contactData, setContactData] = useState(null);
  const [contactTimings, setContactTimings] = useState([]);

  const pageHeaderData = {
    title: {
      marathi: 'संपर्क',
      english: 'Contact Us'
    },
    subtitle: {
      marathi: 'आमच्याशी संपर्क साधा',
      english: 'Get in touch with us'
    }
  };

  // Simulate loading contact data
  useEffect(() => {
    const loadContactData = async () => {
      setIsLoading(true);
      
      try {
        console.log('Starting to load all contact data...');
        
        // Only fetch head office data for the hero section
        const API_BASE = process.env.NODE_ENV === 'production' 
          ? 'https://adminconsole.mlwb.in/api' 
          : 'http://localhost:5000/api';
        const [headOfficeResponse, timingsResponse] = await Promise.all([
          fetch(`${API_BASE}/contact-headoffice`),
          fetch(`${API_BASE}/contact-timings`)
        ]);

        // Process responses
        const headOfficeData = headOfficeResponse.ok ? await headOfficeResponse.json() : [];
        const timingsData = timingsResponse.ok ? await timingsResponse.json() : [];

        // Store raw head office data (transformation will happen during rendering)
        setContactData({ offices: headOfficeData });
        setContactTimings(timingsData);

        // Log successful data loading
        console.log('Contact data loaded successfully:', {
          headOffice: headOfficeData.length,
          timings: timingsData.length
        });

        // Log any failed API calls
        if (!headOfficeResponse.ok) {
          console.error('Failed to load head office:', headOfficeResponse.status);
        }

      } catch (error) {
        console.error('Error loading contact data:', error);
        // Set empty data on error
        setContactData({ offices: [] });
        setContactTimings([]);
      } finally {
        // Loading only ends when ALL data is processed
        console.log('Contact data loading completed');
        setIsLoading(false);
      }
    };

    loadContactData();
  }, []); // Empty dependency array - only run once on mount

  // Auto-index Contact page content for search
  usePageSearchIndex({
    mr: ['संपर्क', 'कार्यालये', 'पत्ता', 'फोन', 'ईमेल', 'नकाशा'],
    en: ['contact', 'offices', 'address', 'phone', 'email', 'map']
  });


  // Helper function to transform head office data based on current language
  const getTransformedOffices = () => {
    return contactData?.offices?.map(office => {
      // Find matching timing data for this office type
      const matchingTiming = contactTimings.find(timing => 
        timing.office_type_english === office.office_type_english ||
        timing.office_type_marathi === office.office_type_marathi
      );
      
      return {
        id: office.id,
        name: isMarathi() ? office.office_type_marathi : office.office_type_english,
        address: isMarathi() ? office.address_marathi : office.address_english,
        phone: isMarathi() ? office.phone_marathi : office.phone_english,
        email: isMarathi() ? office.email_marathi : office.email_english,
        hours: matchingTiming ? (isMarathi() ? matchingTiming.timings_marathi : matchingTiming.timings_english) : '9:45 AM to 5:30 PM',
        weeklyOff: matchingTiming ? (isMarathi() ? matchingTiming.weekly_off_marathi : matchingTiming.weekly_off_english) : 'Saturday, Sunday',
        emergency: false // Default to false since head office doesn't have emergency field
      };
    }) || [];
  };

  // Helper functions for one-click actions
  const handlePhoneClick = (phone) => {
    window.open(`tel:${phone.replace(/\D/g, '')}`, '_self');
  };

  const handleEmailClick = (email) => {
    window.open(`mailto:${email}`, '_self');
  };

  // Accessibility helper functions
  const handleKeyPress = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };


  if (isLoading) {
    return (
      <div className="contact-loading">
        <div className="loading-spinner"></div>
        <p className="loading-text">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="contact">
      <div className="contact-container">
        {/* Page Header */}
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        {/* Hero Section */}
        <section className="hero-section" role="banner" aria-labelledby="hero-title" data-read-section="contact-content">
          <div className="hero-content">
            <div className="hero-info">
              <h2 id="hero-title" className="hero-title">
                {isMarathi() ? 'महाराष्ट्र कामगार कल्याण मंडळ' : 'Maharashtra Labour Welfare Board'}
              </h2>
              
              {/* Quick Contact Cards */}
              <div className="quick-contact-cards">
                {getTransformedOffices().map((office) => (
                  <div key={office.id} className="quick-card">
                    <div className="quick-card-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                      </svg>
                    </div>
                    <div className="quick-card-content">
                      <h3>{office.name}</h3>
                      <p className="quick-card-address">{office.address}</p>
                      <div className="quick-card-actions">
                        <button 
                          className="action-btn phone-btn"
                          onClick={() => handlePhoneClick(office.phone)}
                          onKeyDown={(e) => handleKeyPress(e, () => handlePhoneClick(office.phone))}
                          title={isMarathi() ? 'फोन करा' : 'Call'}
                          aria-label={`${isMarathi() ? 'फोन करा' : 'Call'} ${office.phone}`}
                          tabIndex="0"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                          </svg>
                          {office.phone}
                        </button>
                        <button 
                          className="action-btn email-btn"
                          onClick={() => handleEmailClick(office.email)}
                          onKeyDown={(e) => handleKeyPress(e, () => handleEmailClick(office.email))}
                          title={isMarathi() ? 'ईमेल पाठवा' : 'Send Email'}
                          aria-label={`${isMarathi() ? 'ईमेल पाठवा' : 'Send Email'} ${office.email}`}
                          tabIndex="0"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                          </svg>
                          {office.email}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Interactive Map */}
            <div className="hero-map">
            <div className="map-container">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3172.0340596816673!2d72.832759847098!3d19.0106654327834!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDAwJzM5LjciTiA3MsKwNTAnMDcuNSJF!5e0!3m2!1sen!2sin!4v1466698774573" 
                width="100%" 
                  height="400" 
                frameBorder="0" 
                style={{border: 0}} 
                allowFullScreen
                title="Office Location Map"
              ></iframe>
              </div>
            </div>
            </div>
          </section>

      </div>
    </div>
  );
};

export default Contact;
